import React, { PropsWithChildren, useContext } from 'react';
import { max } from 'lodash';
import { position, rem, size } from 'polished';
import styled, { css } from 'styled-components';

import { circle } from 'helpers/styles/mixins';
import type { Step } from 'components/OnboardingForm/OnboardingSteps';

import { useProgressNavContext } from './ProgressNav';

const NavItem = styled.div<{ isActive?: boolean }>`
  &:not(:last-child) {
    margin-right: ${rem(10)};
    margin-bottom: ${({ isActive }) => rem(isActive ? 21 : 17)};
  }
`;

interface NavItemTextProps {
  isActive?: boolean;
  isCompleted?: boolean;
}

const NavItemIndex = styled.span<NavItemTextProps>`
  ${circle(rem(22))};
  position: relative;
  display: inline-block;
  margin-right: ${rem(7)};
  padding-top: ${rem(4)};
  font-size: ${({ theme }) => theme.fontSizes.dbSmall};
  font-weight: ${({ theme }) => theme.fontWeights.mainMedium};
  text-align: center;
  vertical-align: top;
  background-color: ${({ theme }) => theme.colors.cadetBlue};
  color: ${({ theme }) => theme.colors.white};
  transition: background-color 0.3s ease;
  counter-increment: progress-nav;

  ${({ isActive, theme }) => isActive && `background-color: ${theme.colors.main}`};

  ${({ isCompleted, theme }) =>
    isCompleted
      ? css`
          overflow: hidden;
          text-indent: 200%;
          background-color: ${theme.colors.mountainMeadow};

          &::after {
            ${position('absolute', rem(6), 0, null, 0)};
            ${size(rem(8), rem(5))};
            content: '';
            display: block;
            margin: auto;
            border: solid ${theme.colors.white};
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
          }
        `
      : css`
          &::after {
            content: counter(progress-nav);
          }
        `}
`;

const NavItemName = styled.span<NavItemTextProps>`
  display: inline-block;
  padding-top: ${rem(3)};
  vertical-align: top;
  transition: color 0.3s ease, font-weight 0.3s ease;

  ${({ isActive, isCompleted, theme }) =>
    (isActive || isCompleted) &&
    css`
      font-weight: ${theme.fontWeights.mainMedium};
      color: ${theme.colors.textDark};
    `};
`;

interface ProgressNavItemContext {
  isActive: boolean;
}

export const ProgressNavItemContext = React.createContext<ProgressNavItemContext>({
  isActive: false,
});

export const useProgressNavItemContext = () => useContext(ProgressNavItemContext);

export interface Props extends PropsWithChildren {
  text: React.ReactNode;
  steps: Step[];
}

const ProgressNavItem: React.ComponentType<Props> = ({ text, steps, children }) => {
  const { activeStep } = useProgressNavContext();
  const isActive = activeStep != null && steps.includes(activeStep);
  const lastStep = max(steps);
  const isCompleted = activeStep != null && lastStep != null && lastStep < activeStep;

  return (
    <NavItem isActive={isActive}>
      <NavItemIndex isActive={isActive} isCompleted={isCompleted} />

      <NavItemName isActive={isActive} isCompleted={isCompleted}>
        {text}
      </NavItemName>

      <ProgressNavItemContext.Provider value={{ isActive: Boolean(isActive) }}>
        {children}
      </ProgressNavItemContext.Provider>
    </NavItem>
  );
};

export default ProgressNavItem;
